/**
 * SINGLE SOURCE OF TRUTH for every backend URL used in SK Notification.
 *
 * Two backend services:
 *   PLATFORM_API     → skplatform-api    (auth & identity — refresh / logout /
 *                      me / token; issues the httpOnly cookies directly)
 *   NOTIFICATION_API → sk-notification-api (logs, usage, health, api-keys, notify)
 *
 * Auth/identity call skplatform-api directly (:3013 local), matching SK Catalog.
 * Notification-API calls carry a Bearer platform token (that service's
 * requirePlatformJwt reads the token from Authorization only, not a cookie).
 *
 * Rules: zero hardcoded URLs elsewhere; switch environments by changing ONLY
 * the two base URLs (injected via webpack DefinePlugin).
 */

// ── Base URLs (build-time env, local-dev fallbacks) ────────────────────────
export const PLATFORM_API = process.env.PLATFORM_API_URL || 'http://localhost:3013';
export const NOTIFICATION_API = process.env.NOTIFICATION_API_URL || 'http://localhost:3005';

const P = `${PLATFORM_API}/v1`; // Auth/identity — skplatform-api v1 (direct)
// sk-notification-api mounts its router at the ROOT (app.use('/', routes)) —
// no /api or /v1 prefix. Verified against src/api/routes.ts.
const N = NOTIFICATION_API;

/* ═══ PLATFORM — Auth & Identity (httpOnly cookie session) ═══ */
export const Platform_Me_Url = `${P}/me`;                    // GET — identity probe (200 = in, 401 = out)
export const Platform_Refresh_Token_Url = `${P}/auth/refresh`; // POST — silent rotation
export const Platform_Logout_Url = `${P}/auth/logout`;       // POST — clears session (204)
export const Platform_Verify_Email_Url = `${P}/auth/verify-email`; // POST { token }
export const Platform_Switch_Org_Url = `${P}/auth/switch-org`; // POST { org_id }
// Reads the current access token for cross-origin Bearer use against :3005
// (platform refresh sets a cookie but does not return the token in the body).
export const Platform_Token_Url = `${P}/auth/token`;

/* ═══ NOTIFICATION — Send ═══ */
export const Notify_Url = `${N}/notify`;                     // POST NotifyPayload → { status, jobId }
export const Locales_Url = `${N}/locales`;                   // GET → { supported[], default }
export const Templates_Url = `${N}/templates`;               // GET → { templates[] }

/* ═══ NOTIFICATION — Logs ═══ */
export const Logs_Url = `${N}/logs`;                         // GET ?status&search&dateFrom&dateTo&page&limit
export const Log_Url = (id: string) => `${N}/logs/${id}`;    // GET one
export const Log_Retry_Url = (id: string) => `${N}/logs/${id}/retry`; // POST (currently 501)

/* ═══ NOTIFICATION — Usage / Quotas ═══ */
export const Usage_Url = `${N}/usage`;                       // GET → { products[] }
export const Product_Usage_Url = (productId: string) => `${N}/usage/${productId}`; // GET one product
export const Quota_Invalidate_Url = (productId: string) => `${N}/quota/invalidate/${productId}`; // POST

/* ═══ NOTIFICATION — Admin API keys ═══ */
export const Api_Keys_Url = `${N}/admin/api-keys`;           // GET list / POST issue
export const Api_Key_Url = (id: string) => `${N}/admin/api-keys/${id}`; // DELETE revoke

/* ═══ NOTIFICATION — Health ═══ */
export const Health_Url = `${N}/health`;                     // GET → { status, activeChannels, queue }
export const Health_Circuit_Url = `${N}/health/circuit`;     // GET → { states, providers }
export const Health_Circuit_Reset_Url = (channel: string) => `${N}/health/circuit/${channel}/reset`; // POST
export const Health_Metrics_Url = (window: string) => `${N}/health/metrics?window=${encodeURIComponent(window)}`; // GET

/* ═══ NOTIFICATION — Governance (SKNOTIFICATION-GLOBAL-CR-001) ═══ */
export const Governance_Templates_Url = `${N}/governance/templates`;                        // GET list / POST create
export const Governance_Template_Url = (id: string) => `${N}/governance/templates/${id}`;   // GET detail (template+variants+approvals+permissions)
export const Governance_Variants_Url = (id: string) => `${N}/governance/templates/${id}/variants`; // GET list / PUT upsert
export const Governance_Approval_Url = (id: string) => `${N}/governance/templates/${id}/approval`; // GET list / PUT set (admin)
export const Governance_Permission_Url = (id: string) => `${N}/governance/templates/${id}/permission`; // GET list / PUT set (admin)
export const Governance_Consent_Url = `${N}/governance/consent`;                            // POST record
export const Governance_Suppression_Url = `${N}/governance/suppression`;                    // GET list / POST record
export const Governance_SendDecisions_Url = `${N}/governance/send-decisions`;                // GET ?phone=|?email=
export const Admin_DndScrub_Url = `${N}/admin/dnd-scrub`;                                    // POST upsert (admin)
